import styled from 'styled-components';
import { getColor } from '@karma/components/colors';
import Stack from '@karma/components/Stack';
import Typography from '@karma/components/Typography';

// Spacing constants
const SPACING = {
  FORM_GAP: '24px',
  FIELD_GAP: '12px',
  MOBILE_FIELD_GAP: '8px',
  CHECKBOX_PADDING: '12px 16px',
};

// Breakpoints
const BREAKPOINTS = {
  MOBILE: '576px',
};

export const FormWrapper = styled.div`
  width: 100%;

  /* HubSpot form container */
  .hs-form {
    width: 100%;
    font-family: 'Roboto', sans-serif;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: ${SPACING.FORM_GAP};
  }

  /* Field containers */
  .hs-form-field {
    margin-bottom: 0;
    width: 100%;

    &:last-child {
      margin-bottom: 0;
    }
  }

  /* Fields 2 and 3 in a row */
  .hs-form-field:nth-child(2),
  .hs-form-field:nth-child(3) {
    flex: 0 0 calc(50% - ${SPACING.FIELD_GAP});
    width: calc(50% - ${SPACING.FIELD_GAP});
  }

  /* Hover styles for nth-child(1), nth-child(2), nth-child(3) */
  .hs-form-field:nth-child(1),
  .hs-form-field:nth-child(2),
  .hs-form-field:nth-child(3) {
    input.hs-input:hover:not([name*='LEGAL_CONSENT']),
    textarea.hs-input:hover:not([name*='LEGAL_CONSENT']),
    select.hs-input:hover:not([name*='LEGAL_CONSENT']) {
      border-color: ${getColor('color5')};
      background: ${getColor('color15')};
    }
  }

  /* All other fields full width */
  .hs-form-field:not(:nth-child(2)):not(:nth-child(3)) {
    flex: 0 0 100%;
    width: 100%;
  }

  /* Labels styling according to Figma design */
  label {
    display: block;
    color: ${getColor('color12')};
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    margin-bottom: 8px;
    font-family: 'Roboto', sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.2px;
  }

  /* Remove list markers from checkbox lists */
  .inputs-list,
  ul.inputs-list {
    list-style: none;
    padding-left: 0;
    margin: 0;
  }

  /* Grid layout only for category boxes field */
  .hs-form-field:has(input[name='hs_category_boxes']),
  .hs-form-field:has(input[name*='category_boxes']) {
    .inputs-list,
    ul.inputs-list {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: ${SPACING.FIELD_GAP};

      @media (max-width: ${BREAKPOINTS.MOBILE}) {
        grid-template-columns: repeat(2, 1fr);
        gap: ${SPACING.MOBILE_FIELD_GAP};
      }
    }
  }

  /* Common checkbox selector */
  .hs-form-checkbox,
  li.hs-form-checkbox {
    list-style: none;
    padding-left: 0;
    margin: 0;
    position: relative;
  }

  /* Style checkbox container as button - only for category boxes */
  .hs-form-field:has(input[name='hs_category_boxes']),
  .hs-form-field:has(input[name*='category_boxes']) {
    .hs-form-checkbox label,
    li.hs-form-checkbox label {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 8px;
      padding: ${SPACING.CHECKBOX_PADDING};
      border-radius: 10px;
      border: 2px solid ${getColor('color14')};
      background: ${getColor('color16')};
      color: ${getColor('color12')};
      text-align: center;
      font-family: 'Roboto', sans-serif;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: -0.15px;
      cursor: pointer;
      transition: all 0.2s ease-in-out;
      margin-bottom: 0;
      min-height: 52px;
      width: 100%;
      max-width: 184px;
      box-sizing: border-box;
      white-space: nowrap;
      text-transform: none;

      &:hover {
        border-color: ${getColor('color5')};
        background: ${getColor('color15')};
      }

      /* Icon styling */
      .category-icon {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        object-fit: contain;
      }

      @media (max-width: ${BREAKPOINTS.MOBILE}) {
        padding: 8px 12px;
        font-size: 12px;
        min-height: 44px;
        gap: 6px;

        .category-icon {
          width: 18px;
          height: 18px;
        }
      }
    }

    /* Selected state - when checkbox is checked */
    .hs-form-checkbox input[type='checkbox']:checked + label,
    li.hs-form-checkbox input[type='checkbox']:checked + label,
    .hs-form-checkbox:has(input[type='checkbox']:checked) label,
    li.hs-form-checkbox:has(input[type='checkbox']:checked) label {
      background: ${getColor('color10')};
      border-color: ${getColor('color5')};
      color: ${getColor('color12')};
    }

    /* Focus state for accessibility */
    .hs-form-checkbox input[type='checkbox']:focus + label,
    li.hs-form-checkbox input[type='checkbox']:focus + label {
      outline: 2px solid ${getColor('color5')};
      outline-offset: 2px;
    }

    /* Hide default checkbox input */
    .hs-form-checkbox input[type='checkbox'],
    li.hs-form-checkbox input[type='checkbox'] {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
      margin: 0;
      padding: 0;
    }
  }

  /* Inputs styling */
  input.hs-input:not([name*='LEGAL_CONSENT']),
  textarea.hs-input:not([name*='LEGAL_CONSENT']),
  select.hs-input:not([name*='LEGAL_CONSENT']) {
    width: 100%;
    padding: 12px 16px;

    border-radius: 10px;
    border: 1px solid ${getColor('color14')};
    background: ${getColor('color15')};
    color: ${getColor('color12')};
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    font-family: 'Roboto', sans-serif;
    transition: all 0.2s ease-in-out;
    box-sizing: border-box;

    &::placeholder {
      color: ${getColor('neutral2')};
      opacity: 1;
    }

    &:focus {
      outline: none;
      border-color: ${getColor('color5')};
    }

    /* Error state - red border */
    &:invalid:not(:focus):not(:placeholder-shown) {
      border-color: ${getColor('color7')};
    }
  }

  .hs-error-msg {
    color: ${getColor('color7')};
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    margin-top: 4px;
    width: 100%;
    text-transform: none;
  }

  /* Legal Consent dependent fields styling */
  .hs-dependent-field:has(input[name*='LEGAL_CONSENT']),
  .hs-dependent-field:has(textarea[name*='LEGAL_CONSENT']),
  .hs-dependent-field:has(select[name*='LEGAL_CONSENT']) {
    .hs-form-booleancheckbox-display {
      display: flex;
      flex-direction: row;
      gap: 4px;
      align-items: center;
    }

    label {
      text-align: left;

      span {
        margin: 0;
      }

      p {
        color: ${getColor('color2')};
        text-transform: none;
        font-size: 14px;
        font-weight: 400;
        line-height: 23px;
        letter-spacing: -0.15px;
      }

      input[type='checkbox'] {
        width: 20px;
        height: 20px;
      }
    }
  }

  /* Rich text fields styling */
  .hs-richtext {
    p {
      color: ${getColor('color12')};
      margin-left: 26px;
      margin-bottom: 0;
      padding-right: 95px;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 23px;
      letter-spacing: -0.15px;
    }
  }

  /* Submit button container */
  .hs_submit {
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }

  /* Submit button styling */
  input[type='submit'].hs-button.primary.large {
    padding: 12px 32px;
    width: 201px;
    background-color: ${getColor('color2')};
    color: ${getColor('color3')};
    justify-content: center;
    align-items: center;
    border-radius: 100px;
    border: none;
    transition: all 0.2s ease-in-out;

    &:hover {
      background-color: ${getColor('color6')};
      color: ${getColor('color2')};
    }
  }

  /* Validation error styling with higher specificity */
  .hs-error-msg.hs-validation-error-red,
  .hs-error-msgs .hs-validation-error-red,
  .hs-error-msgs li.hs-validation-error-red,
  .hs-main-font-element.hs-validation-error-red {
    color: ${getColor('color7')};
  }
`;

export const HeaderContainer = styled(Stack).attrs({
  direction: 'column',
  gap: 12,
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  fullWidth: true,
})`
  margin-bottom: 32px;
`;

export const HeaderTitle = styled(Typography).attrs({
  variant: 'paragraphMedium',
})`
  line-height: 24px;
`;

export const HeaderDescription = styled(Typography).attrs({
  variant: 'paragraphMedium',
  color: 'color12',
})`
  line-height: 24px;
`;
